/**
 * Dashboard Page - Bento Grid Layout
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { healthAPI, adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';
import PushNotificationBanner from '../components/PushNotificationBanner';
import LanguageSelector from '../components/LanguageSelector';
import { toast } from 'sonner';
import DeliveryPhotoVerification from '../components/DeliveryPhotoVerification';
import { 
  Link2, 
  LogOut, 
  User, 
  Shield, 
  Activity, 
  Users,
  Settings,
  ChevronRight,
  Truck,
  Store,
  Package,
  MapPin,
  ShoppingBag,
  ShoppingCart,
  ClipboardList,
  LayoutDashboard,
  TrendingUp,
  DollarSign,
  MessageCircle,
  Star,
  AlertTriangle,
  Gift,
  Camera,
  Headphones,
  Car,
  Building2,
  Newspaper,
  UserPlus,
  Clock
} from 'lucide-react';

const Dashboard = () => {
  const { user, logout, isAdmin, hasRole, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [appearance, setAppearance] = useState(null);
  
  // Application status tracking
  const [applicationStatus, setApplicationStatus] = useState({
    driver: null, // 'not_started' | 'pending' | 'approved'
    seller: null,
    pudo: null
  });
  
  // Contract Hire eligibility (server-authoritative)
  const [contractHireEligibility, setContractHireEligibility] = useState({
    loading: true,
    eligible: false,
    reason: null,
    pilotMode: false
  });

  useEffect(() => {
    // Refresh user to get latest roles (e.g., after driver approval)
    if (refreshUser) refreshUser();
    fetchHealth();
    fetchAppearance();
    fetchApplicationStatus();
    fetchContractHireEligibility();
    if (isAdmin()) {
      fetchStats();
    }
  }, [isAdmin, refreshUser]);
  
  const fetchContractHireEligibility = async () => {
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/contract-hire/eligibility', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContractHireEligibility({
          loading: false,
          eligible: data.eligible,
          reason: data.reason,
          pilotMode: data.pilot_mode,
          allowedParishes: data.allowed_parishes || []
        });
        // Log CTA viewed event
        if (data.eligible) {
          logContractHireEvent('CH_DASHBOARD_CTA_VIEWED');
        }
      } else {
        setContractHireEligibility({ loading: false, eligible: false, reason: 'Service unavailable' });
      }
    } catch (e) {
      setContractHireEligibility({ loading: false, eligible: false, reason: 'Service unavailable' });
    }
  };
  
  const logContractHireEvent = async (eventType) => {
    try {
      await fetch(process.env.REACT_APP_BACKEND_URL + `/api/contract-hire/analytics/event?event_type=${eventType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (e) { /* silent */ }
  };
  
  const handleContractHireCTAClick = () => {
    logContractHireEvent('CH_DASHBOARD_CTA_CLICKED');
    navigate('/hire/new');
  };

  const fetchApplicationStatus = async () => {
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/users/my-applications', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setApplicationStatus({
          driver: data.driver_status || 'not_started',
          seller: data.seller_status || 'not_started',
          pudo: data.pudo_status || 'not_started'
        });
      }
    } catch (e) { 
      // If endpoint doesn't exist, default to not_started
      setApplicationStatus({
        driver: hasRole("DRIVER") ? 'approved' : 'not_started',
        seller: (hasRole("SELLER") || hasRole("MERCHANT") || hasRole("BUSINESS_MERCHANT")) ? 'approved' : 'not_started',
        pudo: (hasRole("PUDO_POINT") || hasRole("PUDO_OPERATOR")) ? 'approved' : 'not_started'
      });
    }
  };

  const fetchAppearance = async () => {
    try {
      const res = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/settings/appearance');
      const data = await res.json();
      setAppearance(data);
    } catch (e) { /* silent */ }
  };

  const fetchHealth = async () => {
    try {
      const data = await healthAPI.check();
      setHealth(data);
    } catch (error) {
      console.error('Health check failed:', error);
    }
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const data = await adminAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Stats fetch failed:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      ADMIN: 'bg-red-500/20 text-red-400 border-red-500/30',
      CEO: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      DRIVER: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      BUSINESS_MERCHANT: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      CUSTOMER: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      PUDO_POINT: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      FULFILLMENT: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    };
    return colors[role] || colors.CUSTOMER;
  };

  const themeStyle = appearance ? {
    backgroundColor: appearance.bg_primary,
    color: appearance.text_primary,
  } : {};

  return (
    <div className="min-h-screen bg-slate-950" style={themeStyle}>
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-jamaica shadow-lg"><Link2 className="w-5 h-5 text-black" /></div>
            <span className="text-xl font-bold text-white tracking-tight font-['Manrope']">
              
            </span>
            <span className="hidden sm:inline-block text-xs font-mono text-slate-500 bg-slate-800 px-2 py-1 rounded">
              CONTROL ROOM
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm font-[Manrope] hidden sm:block">
              {user?.name}
            </span>
            <LanguageSelector variant="minimal" />
            <NotificationBell />
            <Button
              data-testid="settings-btn"
              onClick={() => navigate('/settings')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              title="Settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button
              data-testid="settings-btn"
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="dashboard-main">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight font-['Manrope'] mb-2">
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-400 font-['Manrope']">
            Manage your logistics operations from the control room
          </p>
        </div>

        {/* Push Notification Banner */}
        <PushNotificationBanner />

        {/* Refer & Earn banner */}
        <button
          data-testid="refer-earn-banner"
          onClick={() => navigate('/refer')}
          className="group w-full mb-6 text-left rounded-2xl overflow-hidden border border-amber-500/40 bg-gradient-to-r from-amber-500/15 via-emerald-500/10 to-slate-900 hover:border-amber-400/70 transition-colors"
        >
          <div className="flex items-center gap-4 px-5 py-4">
            <div className="h-12 w-12 shrink-0 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Gift className="h-6 w-6 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold font-['Manrope'] leading-tight">
                Refer &amp; earn <span className="text-amber-400">20% off</span>
              </p>
              <p className="text-slate-400 text-sm truncate">
                Your friend gets 20% off their first delivery — you get 20% off your next. Top referrer each month wins J$2,000.
              </p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1 text-amber-400 text-sm font-semibold group-hover:gap-2 transition-all">
              Invite friends <ChevronRight className="h-4 w-4" />
            </span>
          </div>
        </button>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          {/* Profile Card - Spans 2 cols */}
          <div 
            data-testid="profile-card"
            className="md:col-span-2 bg-slate-900/50 border border-slate-800 rounded-md p-6 hover:border-slate-700 transition-colors"
          >
            <div className="flex items-start gap-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <User className="h-8 w-8 text-slate-950" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-white font-['Manrope'] mb-1">
                  {user?.name}
                </h2>
                <p className="text-slate-400 text-sm font-mono mb-1">
                  {user?.email || user?.phone}
                </p>
                {user?.member_id && (
                  <p className="text-amber-500 text-sm font-mono font-semibold mb-3">
                    {user.member_id}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {user?.roles?.map((role) => (
                    <span 
                      key={role}
                      className={`px-2 py-0.5 rounded-full text-xs font-mono border ${getRoleBadgeColor(role)}`}
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-800 grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500 font-['Manrope']">User ID</span>
                <p className="text-slate-300 font-mono text-xs mt-1 truncate">{user?.id}</p>
              </div>
              <div>
                <span className="text-slate-500 font-['Manrope']">Status</span>
                <p className="text-teal-400 font-mono text-xs mt-1 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-teal-400 animate-pulse" />
                  ACTIVE
                </p>
              </div>
            </div>
          </div>

          {/* System Status Card */}
          <div 
            data-testid="health-card"
            className="bg-slate-900/50 border border-slate-800 rounded-md p-6 hover:border-slate-700 transition-colors"
          >
            <div className="flex items-center gap-3 mb-4">
              <Activity className="h-5 w-5 text-amber-500" />
              <h3 className="text-sm font-medium text-white font-['Manrope'] uppercase tracking-wide">
                System Status
              </h3>
            </div>
            {health ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">API</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${
                    health.status === 'ok' 
                      ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' 
                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}>
                    {health.status?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Database</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${
                    health.database === 'connected' 
                      ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' 
                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}>
                    {health.database?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Environment</span>
                  <span className="text-amber-400 font-mono text-xs">
                    {health.environment?.toUpperCase()}
                  </span>
                </div>
              </div>
            ) : (
              <div className="animate-pulse space-y-3">
                <div className="h-6 bg-slate-800 rounded" />
                <div className="h-6 bg-slate-800 rounded" />
              </div>
            )}
          </div>

          {/* Admin Access Card */}
          {isAdmin() && (
            <div 
              data-testid="admin-card"
              className="bg-slate-900/50 border border-slate-800 rounded-md p-6 hover:border-slate-700 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <Shield className="h-5 w-5 text-red-400" />
                <h3 className="text-sm font-medium text-white font-['Manrope'] uppercase tracking-wide">
                  Admin Access
                </h3>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                You have administrative privileges
              </p>
              <div className="space-y-2">
                <Button
              data-testid="settings-btn"
                  data-testid="ceo-dashboard-btn"
                  onClick={() => navigate('/ceo')}
                  className="w-full bg-emerald-600 text-white hover:bg-emerald-700 border border-emerald-500 rounded-sm h-10 font-medium tracking-wide"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  CEO Dashboard
                  <ChevronRight className="h-4 w-4 ml-auto" />
                </Button>
                <Button
              data-testid="settings-btn"
                  data-testid="finance-btn"
                  onClick={() => navigate('/admin/finance')}
                  className="w-full bg-amber-600 text-white hover:bg-amber-700 border border-amber-500 rounded-sm h-10 font-medium tracking-wide"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Finance Dashboard
                  <ChevronRight className="h-4 w-4 ml-auto" />
                </Button>
                <Button
              data-testid="settings-btn"
                  data-testid="admin-panel-btn"
                  onClick={() => navigate('/admin')}
                  className="w-full bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700 rounded-sm h-10 font-medium tracking-wide"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Admin Panel
                  <ChevronRight className="h-4 w-4 ml-auto" />
                </Button>
                <Button
                  data-testid="c2c-rates-btn"
                  onClick={() => navigate("/admin/shipping")}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 border border-emerald-500 rounded-sm h-12 font-medium tracking-wide relative overflow-hidden"
                >
                  <Truck className="h-4 w-4 mr-2" />
                  C2C Shipping Rates
                  <span className="absolute -top-1 -right-1 px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-yellow-400 to-orange-500 text-black rounded-bl-lg rounded-tr-sm animate-pulse shadow-lg">NEW</span>
                  <ChevronRight className="h-4 w-4 ml-auto" />
                </Button>
              </div>
            </div>
          )}

          {/* Comprehensive Stats Card - Admin Only, Spans full width */}
          {isAdmin() && stats && (
            <div 
              data-testid="stats-card"
              className="md:col-span-4 bg-slate-900/50 border border-slate-800 rounded-md p-6 hover:border-slate-700 transition-colors"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <TrendingUp className="h-5 w-5 text-amber-500" />
                  <h3 className="text-sm font-medium text-white font-['Manrope'] uppercase tracking-wide">
                    Platform Statistics
                  </h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={fetchStats}
                  disabled={loadingStats}
                  className="text-slate-400 hover:text-white"
                >
                  {loadingStats ? 'Refreshing...' : 'Refresh'}
                </Button>
              </div>
              
              {/* User Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-800/50 rounded p-4">
                  <p className="text-3xl font-bold text-white font-['JetBrains_Mono']">
                    {stats.total_users}
                  </p>
                  <p className="text-slate-400 text-xs uppercase tracking-wide mt-1">Total Users</p>
                </div>
                <div className="bg-slate-800/50 rounded p-4">
                  <p className="text-3xl font-bold text-teal-400 font-['JetBrains_Mono']">
                    {stats.active_users}
                  </p>
                  <p className="text-slate-400 text-xs uppercase tracking-wide mt-1">Active Users</p>
                </div>
                <div className="bg-slate-800/50 rounded p-4">
                  <p className="text-3xl font-bold text-amber-400 font-['JetBrains_Mono']">
                    {stats.users_by_role?.DRIVER || 0}
                  </p>
                  <p className="text-slate-400 text-xs uppercase tracking-wide mt-1">Drivers</p>
                </div>
                <div className="bg-slate-800/50 rounded p-4">
                  <p className="text-3xl font-bold text-emerald-400 font-['JetBrains_Mono']">
                    {stats.users_by_role?.BUSINESS_MERCHANT || 0}
                  </p>
                  <p className="text-slate-400 text-xs uppercase tracking-wide mt-1">Merchants</p>
                </div>
              </div>
              
              {/* Packages & Revenue Row */}
              {(stats.packages || stats.revenue) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-700/30 rounded p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-blue-400" />
                      <span className="text-blue-400 text-xs uppercase tracking-wide">Packages Today</span>
                    </div>
                    <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                      {stats.packages?.today || 0}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-700/30 rounded p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-purple-400" />
                      <span className="text-purple-400 text-xs uppercase tracking-wide">This Month</span>
                    </div>
                    <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                      {stats.packages?.this_month || 0}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/30 rounded p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-green-400" />
                      <span className="text-green-400 text-xs uppercase tracking-wide">Revenue Today</span>
                    </div>
                    <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                      J${(stats.revenue?.today || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-700/30 rounded p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-amber-400" />
                      <span className="text-amber-400 text-xs uppercase tracking-wide">This Month</span>
                    </div>
                    <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">
                      J${(stats.revenue?.this_month || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Driver & PUDO Status Row */}
              {(stats.drivers || stats.pudos) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-800/50 rounded p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-xs uppercase">Drivers Online</span>
                      <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
                    </div>
                    <p className="text-2xl font-bold text-green-400 font-['JetBrains_Mono'] mt-2">
                      {stats.drivers?.online || 0}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">of {stats.drivers?.total || 0} total</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-xs uppercase">Pending Apps</span>
                      {(stats.drivers?.pending_applications || 0) > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded-full">{stats.drivers?.pending_applications}</span>
                      )}
                    </div>
                    <p className="text-2xl font-bold text-orange-400 font-['JetBrains_Mono'] mt-2">
                      {stats.drivers?.pending_applications || 0}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">driver applications</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-4">
                    <span className="text-slate-400 text-xs uppercase">Active PUDOs</span>
                    <p className="text-2xl font-bold text-cyan-400 font-['JetBrains_Mono'] mt-2">
                      {stats.pudos?.active || 0}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">of {stats.pudos?.total || 0} total</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-4">
                    <span className="text-slate-400 text-xs uppercase">Total Packages</span>
                    <p className="text-2xl font-bold text-white font-['JetBrains_Mono'] mt-2">
                      {stats.packages?.total || 0}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">all time</p>
                  </div>
                </div>
              )}
              
              {/* Package Status Breakdown */}
              {stats.packages?.by_status && Object.keys(stats.packages.by_status).length > 0 && (
                <div className="border-t border-slate-700 pt-4">
                  <h4 className="text-slate-400 text-xs uppercase tracking-wide mb-3">Package Status Breakdown</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.packages.by_status).map(([status, count]) => (
                      <span 
                        key={status}
                        className={`px-3 py-1.5 rounded-full text-xs font-mono ${
                          status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                          status === 'in_transit' ? 'bg-blue-500/20 text-blue-400' :
                          status === 'picked_up' ? 'bg-purple-500/20 text-purple-400' :
                          status === 'assigned' ? 'bg-amber-500/20 text-amber-400' :
                          status === 'pending' ? 'bg-slate-500/20 text-slate-400' :
                          status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}
                      >
                        {status.replace('_', ' ')}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quick Actions - Module Links */}
          <div 
            data-testid="modules-card"
            className="md:col-span-2 bg-slate-900/50 border border-slate-800 rounded-md p-6 hover:border-slate-700 transition-colors"
          >
            <h3 className="text-sm font-medium text-white font-['Manrope'] uppercase tracking-wide mb-4">
              Quick Actions
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {hasRole("DRIVER") && (
              <button 
                onClick={() => navigate('/driver')}
                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded border border-slate-800/50 hover:border-blue-500/50 hover:bg-slate-800/50 transition-all text-left"
              >
                <Truck className="h-5 w-5 text-blue-400" />
                <span className="text-slate-300 text-sm">Driver Portal</span>
              </button>
              )}
              {(hasRole("PUDO_POINT") || hasRole("PUDO_OPERATOR")) && (
              <button 
                onClick={() => navigate('/pudo')}
                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded border border-slate-800/50 hover:border-amber-500/50 hover:bg-slate-800/50 transition-all text-left"
              >
                <Store className="h-5 w-5 text-amber-400" />
                <span className="text-slate-300 text-sm">PUDO Centre</span>
              </button>
              )}
              {(hasRole("SELLER") || hasRole("seller") || hasRole("MERCHANT") || hasRole("BUSINESS_MERCHANT")) && (
              <button 
                onClick={() => navigate('/seller/dashboard')}
                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded border border-slate-800/50 hover:border-emerald-500/50 hover:bg-slate-800/50 transition-all text-left"
              >
                <Store className="h-5 w-5 text-emerald-400" />
                <span className="text-slate-300 text-sm">Seller Dashboard</span>
              </button>
              )}
              <button 
                onClick={() => navigate('/send')}
                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded border border-slate-800/50 hover:border-amber-500/50 hover:bg-slate-800/50 transition-all text-left"
              >
                <Package className="h-5 w-5 text-amber-400" />
                <span className="text-slate-300 text-sm">Send Package</span>
              </button>
              
              {/* Contract Hire CTA - Server-authoritative gating */}
              {!contractHireEligibility.loading && contractHireEligibility.eligible && (
                <button 
                  onClick={handleContractHireCTAClick}
                  data-testid="contract-hire-cta"
                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded border border-purple-500/50 hover:border-purple-400 hover:bg-purple-900/40 transition-all text-left relative"
                >
                  <Clock className="h-5 w-5 text-purple-400" />
                  <span className="text-purple-200 text-sm font-medium">Book Contract Hire</span>
                  <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[9px] font-bold bg-purple-500 text-white rounded-full">NEW</span>
                </button>
              )}
              
              <button 
                onClick={() => navigate('/packages')}
                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded border border-slate-800/50 hover:border-red-500/50 hover:bg-slate-800/50 transition-all text-left"
              >
                <MapPin className="h-5 w-5 text-red-400" />
                <span className="text-slate-300 text-sm">My Packages</span>
              </button>
              <button 
                onClick={() => navigate('/events/create')}
                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded border border-slate-800/50 hover:border-purple-500/50 hover:bg-slate-800/50 transition-all text-left"
              >
                <span className="text-lg">🎉</span>
                <span className="text-slate-300 text-sm">Post Event</span>
              </button>
              <button 
                onClick={() => navigate('/withdrawals')}
                className="flex items-center gap-3 p-3 bg-slate-800/30 rounded border border-slate-800/50 hover:border-green-500/50 hover:bg-slate-800/50 transition-all text-left"
              >
                <span className="text-lg">💰</span>
                <span className="text-slate-300 text-sm">Withdraw</span>
              </button>
            </div>
          </div>

          {/* Join the Liantiao Network - Premium Role Cards with Status */}
          <div 
            data-testid="join-network-card"
            className="md:col-span-2 bg-gradient-to-br from-slate-900/80 to-slate-800/50 border border-slate-700/50 rounded-lg p-6"
          >
            <div className="text-center mb-6">
              <p className="text-slate-400 text-sm tracking-wide">Choose how you want to participate in the Liantiao network</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* DRIVER CARD */}
              {hasRole("DRIVER") ? (
                <div className="bg-blue-900/20 border border-blue-700/40 rounded-lg p-5 relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full border border-emerald-500/30">Approved</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Car className="h-6 w-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Delivery Link</h4>
                      <p className="text-slate-400 text-sm mb-2">You're powering Jamaica's delivery chain</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
                        Accept jobs • Track deliveries • Collect earnings
                      </p>
                      <button 
                        onClick={() => navigate('/driver')}
                        className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
                        data-testid="driver-dashboard-btn"
                      >
                        Enter Driver Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              ) : applicationStatus.driver === 'pending' ? (
                <div className="bg-slate-800/30 border border-yellow-700/40 rounded-lg p-5 relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-medium rounded-full border border-yellow-500/30">Pending Review</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                      <Car className="h-6 w-6 text-yellow-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Become a Delivery Link</h4>
                      <p className="text-slate-300 text-sm mb-2">Earn by moving packages across your area</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-yellow-400"></span>
                        Apply → Verify documents → Get approved → Start earning
                      </p>
                      <button 
                        disabled
                        className="w-full py-2 px-4 bg-slate-700 text-slate-400 text-sm font-medium rounded-lg cursor-not-allowed"
                        data-testid="apply-driver-btn"
                      >
                        Application Under Review
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-5 hover:border-blue-500/50 transition-all group relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-slate-600/30 text-slate-500 text-xs font-medium rounded-full border border-slate-600/30">Not Started</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/20 flex items-center justify-center transition-colors">
                      <Car className="h-6 w-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Become a Delivery Link</h4>
                      <p className="text-slate-300 text-sm mb-2">Earn by moving packages across your area</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-slate-400"></span>
                        Apply → Verify documents → Get approved → Start earning
                      </p>
                      <button 
                        onClick={() => navigate('/dashboard/apply/driver')}
                        className="w-full py-2 px-4 bg-blue-600/80 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
                        data-testid="apply-driver-btn"
                      >
                        Apply to Drive
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* SELLER CARD */}
              {(hasRole("SELLER") || hasRole("seller") || hasRole("MERCHANT") || hasRole("BUSINESS_MERCHANT")) ? (
                <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg p-5 relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full border border-emerald-500/30">Approved</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Store className="h-6 w-6 text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Your Store</h4>
                      <p className="text-slate-400 text-sm mb-2">Reach customers with built-in delivery</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
                        Add products • Process orders • Track payouts
                      </p>
                      <button 
                        onClick={() => navigate('/seller/dashboard')}
                        className="w-full py-2 px-4 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
                        data-testid="seller-dashboard-btn"
                      >
                        Manage Store
                      </button>
                    </div>
                  </div>
                </div>
              ) : applicationStatus.seller === 'pending' ? (
                <div className="bg-slate-800/30 border border-yellow-700/40 rounded-lg p-5 relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-medium rounded-full border border-yellow-500/30">Pending Review</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                      <Store className="h-6 w-6 text-yellow-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Sell Through the Chain</h4>
                      <p className="text-slate-300 text-sm mb-2">Reach customers with built-in delivery</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-yellow-400"></span>
                        Apply → Set up store → List products → Start selling
                      </p>
                      <button 
                        disabled
                        className="w-full py-2 px-4 bg-slate-700 text-slate-400 text-sm font-medium rounded-lg cursor-not-allowed"
                        data-testid="apply-seller-btn"
                      >
                        Application Under Review
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-5 hover:border-emerald-500/50 transition-all group relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-slate-600/30 text-slate-500 text-xs font-medium rounded-full border border-slate-600/30">Not Started</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 group-hover:bg-emerald-500/20 flex items-center justify-center transition-colors">
                      <Store className="h-6 w-6 text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Sell Through the Chain</h4>
                      <p className="text-slate-300 text-sm mb-2">Reach customers with built-in delivery</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-slate-400"></span>
                        Apply → Set up store → List products → Start selling
                      </p>
                      <button 
                        onClick={() => navigate('/dashboard/apply/seller')}
                        className="w-full py-2 px-4 bg-emerald-600/80 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
                        data-testid="apply-seller-btn"
                      >
                        Open a Store
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* PUDO CARD */}
              {(hasRole("PUDO_POINT") || hasRole("PUDO_OPERATOR")) ? (
                <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-5 relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full border border-emerald-500/30">Approved</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Your Pickup Point</h4>
                      <p className="text-slate-400 text-sm mb-2">Serving your community as a parcel hub</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
                        Receive packages • Verify handover • Earn per parcel
                      </p>
                      <button 
                        onClick={() => navigate('/pudo')}
                        className="w-full py-2 px-4 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-lg transition-colors"
                        data-testid="pudo-dashboard-btn"
                      >
                        Manage Pickup Point
                      </button>
                    </div>
                  </div>
                </div>
              ) : applicationStatus.pudo === 'pending' ? (
                <div className="bg-slate-800/30 border border-yellow-700/40 rounded-lg p-5 relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-medium rounded-full border border-yellow-500/30">Pending Review</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-yellow-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Host a Pickup Point</h4>
                      <p className="text-slate-300 text-sm mb-2">Turn your location into a parcel hub</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-yellow-400"></span>
                        Apply → Confirm location → Get verified → Go live
                      </p>
                      <button 
                        disabled
                        className="w-full py-2 px-4 bg-slate-700 text-slate-400 text-sm font-medium rounded-lg cursor-not-allowed"
                        data-testid="apply-pudo-btn"
                      >
                        Application Under Review
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-5 hover:border-amber-500/50 transition-all group relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-slate-600/30 text-slate-500 text-xs font-medium rounded-full border border-slate-600/30">Not Started</span>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500/10 group-hover:bg-amber-500/20 flex items-center justify-center transition-colors">
                      <Building2 className="h-6 w-6 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-semibold mb-1">Host a Pickup Point</h4>
                      <p className="text-slate-300 text-sm mb-2">Turn your location into a parcel hub</p>
                      <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-slate-400"></span>
                        Apply → Confirm location → Get verified → Go live
                      </p>
                      <button 
                        onClick={() => navigate('/dashboard/apply/pudo')}
                        className="w-full py-2 px-4 bg-amber-600/80 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
                        data-testid="apply-pudo-btn"
                      >
                        Register Location
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* CLASSIFIED CARD */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-5 hover:border-cyan-500/50 transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 group-hover:bg-cyan-500/20 flex items-center justify-center transition-colors">
                    <Newspaper className="h-6 w-6 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-semibold mb-1">List an Item</h4>
                    <p className="text-slate-300 text-sm mb-2">Sell locally with secure delivery</p>
                    <p className="text-slate-500 text-xs mb-3 flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-slate-400"></span>
                      Post item → Connect with buyer → Complete handover
                    </p>
                    <button 
                      onClick={() => navigate('/classifieds/post')}
                      className="w-full py-2 px-4 bg-cyan-600/80 hover:bg-cyan-600 text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid="post-classified-btn"
                    >
                      Create Listing
                    </button>
                  </div>
                </div>
              </div>

            </div>

            <div className="text-center mt-6 space-y-2">
              <p className="text-slate-500 text-xs tracking-wide italic">Every role strengthens the chain.</p>
              <p className="text-slate-600 text-xs">Built in Jamaica. Designed to reduce theft with verified handover.</p>
            </div>
          </div>

          
          {/* My Account Section */}
          <div 
            data-testid="account-card"
            className="md:col-span-2 bg-gradient-to-br from-purple-900/50 to-purple-800/30 border border-purple-700/50 rounded-md p-6 hover:border-purple-600/50 transition-colors"
          >
            <h3 className="text-sm font-medium text-white font-[Manrope] uppercase tracking-wide mb-4 flex items-center gap-2">
              <User className="h-4 w-4 text-purple-400" />
              My Account
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <button 
                onClick={() => navigate("/profile")}
                className="flex flex-col items-center gap-2 p-4 bg-purple-800/30 rounded-lg border border-purple-700/50 hover:border-purple-500/70 hover:bg-purple-800/50 transition-all"
              >
                <Camera className="h-6 w-6 text-purple-400" />
                <span className="text-slate-200 text-sm font-medium">Profile</span>
              </button>
              <button 
                onClick={() => navigate("/my-reviews")}
                className="flex flex-col items-center gap-2 p-4 bg-yellow-800/30 rounded-lg border border-yellow-700/50 hover:border-yellow-500/70 hover:bg-yellow-800/50 transition-all"
              >
                <Star className="h-6 w-6 text-yellow-400" />
                <span className="text-slate-200 text-sm font-medium">My Reviews</span>
              </button>
              <button 
                onClick={() => navigate("/my-disputes")}
                className="flex flex-col items-center gap-2 p-4 bg-orange-800/30 rounded-lg border border-orange-700/50 hover:border-orange-500/70 hover:bg-orange-800/50 transition-all"
              >
                <AlertTriangle className="h-6 w-6 text-orange-400" />
                <span className="text-slate-200 text-sm font-medium">Disputes</span>
              </button>
              <button 
                onClick={() => navigate("/rewards")}
                className="flex flex-col items-center gap-2 p-4 bg-purple-800/30 rounded-lg border border-purple-700/50 hover:border-purple-500/70 hover:bg-purple-800/50 transition-all"
              >
                <Gift className="h-6 w-6 text-purple-400" />
                <span className="text-slate-200 text-sm font-medium">Rewards</span>
              </button>
            </div>
          </div>

          {/* Messages & Support Section */}
          <div 
            data-testid="messaging-card"
            className="md:col-span-2 bg-gradient-to-br from-[#00A86B]/20 to-[#00A86B]/10 border border-[#00A86B]/50 rounded-md p-6 hover:border-[#00A86B]/70 transition-colors"
          >
            <h3 className="text-sm font-medium text-white font-['Manrope'] uppercase tracking-wide mb-4 flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-[#00A86B]" />
              Messages & Support
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <button 
                onClick={() => navigate('/messages')}
                className="flex flex-col items-center gap-2 p-4 bg-[#00A86B]/20 rounded-lg border border-[#00A86B]/50 hover:border-[#00A86B]/70 hover:bg-[#00A86B]/30 transition-all"
              >
                <MessageCircle className="h-6 w-6 text-[#00A86B]" />
                <span className="text-slate-200 text-sm font-medium">Messages</span>
              </button>
              <button 
                onClick={() => navigate('/support')}
                className="flex flex-col items-center gap-2 p-4 bg-[#FFD300]/20 rounded-lg border border-[#FFD300]/50 hover:border-[#FFD300]/70 hover:bg-[#FFD300]/30 transition-all"
              >
                <Headphones className="h-6 w-6 text-[#FFD300]" />
                <span className="text-slate-200 text-sm font-medium">Get Support</span>
              </button>
              <button 
                onClick={() => navigate("/package-chats")}
                className="flex flex-col items-center gap-2 p-4 bg-blue-500/20 rounded-lg border border-blue-500/50 hover:border-blue-500/70 hover:bg-blue-500/30 transition-all"
              >
                <Package className="h-6 w-6 text-blue-400" />
                <span className="text-slate-200 text-sm font-medium">Package Chats</span>
              </button>
            </div>
          </div>

          {/* Marketplace Section */}
          <div 
            data-testid="marketplace-card"
            className="md:col-span-2 bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 border border-emerald-700/50 rounded-md p-6 hover:border-emerald-600/50 transition-colors"
          >
            <h3 className="text-sm font-medium text-white font-['Manrope'] uppercase tracking-wide mb-4 flex items-center gap-2">
              <ShoppingBag className="h-4 w-4 text-emerald-400" />
              Marketplace
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <button 
                onClick={() => navigate('/shop')}
                className="flex flex-col items-center gap-2 p-4 bg-emerald-800/30 rounded-lg border border-emerald-700/50 hover:border-emerald-500/70 hover:bg-emerald-800/50 transition-all"
              >
                <ShoppingBag className="h-6 w-6 text-emerald-400" />
                <span className="text-slate-200 text-sm font-medium">Shop Now</span>
              </button>
              <button 
                onClick={() => navigate('/cart')}
                className="flex flex-col items-center gap-2 p-4 bg-emerald-800/30 rounded-lg border border-emerald-700/50 hover:border-emerald-500/70 hover:bg-emerald-800/50 transition-all"
              >
                <ShoppingCart className="h-6 w-6 text-emerald-400" />
                <span className="text-slate-200 text-sm font-medium">My Cart</span>
              </button>
              <button 
                onClick={() => navigate('/orders')}
                className="flex flex-col items-center gap-2 p-4 bg-emerald-800/30 rounded-lg border border-emerald-700/50 hover:border-emerald-500/70 hover:bg-emerald-800/50 transition-all"
              >
                <ClipboardList className="h-6 w-6 text-emerald-400" />
                <span className="text-slate-200 text-sm font-medium">My Orders</span>
              </button>
            </div>
          </div>

          {/* Delivery Photo Verification Section */}
          <div className="md:col-span-2">
            <DeliveryPhotoVerification />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
